import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from '@gatsbyjs/reach-router';
import { Link } from 'gatsby';
import { Heading, Spinner } from '@printdeal/ui-library';
import { useSelector } from 'react-redux';
import { SelectorContent } from '@printdeal/product-selector';
import { klona } from 'klona';
import { useSelector as useReduxSelector } from 'react-redux/es/hooks/useSelector';
import { SegmentVariants, type VariantFiltersById } from '@printdeal/segmentation/react';
import { useLatestSelection } from '../../../../hooks/query/useLatestSelection';
import useSystemPages from '../../../../hooks/useSystemPages';
import { IProductRoute } from '../../../../types/product/productDetailPage';
import { selectors as authSelectors } from '../../../../store/slices/authSlice';
import { ProductRoute, ProductRouteItem } from '../../selector/components/ProductRoute';
import { dispatchDataLayerEntry } from '../../../../helpers/dataLayerHelpers/productDataLayerHelper';
import { validateRoutes } from '../../selector/helpers/ProductRouteHelper';
import { RootState } from '../../../../store';

const variantFilters: VariantFiltersById[] = [{
  id: 'latest-selection-filter-end-users',
  filters: {
    isEndUser: true,
    ordersLifetime: {
      min: 1,
      allowEmpty: false,
    },
  },
}];

interface Props {
  selectorConfig: SelectorContent;
  productKey: string;
  productRoute: IProductRoute;
  contentfulId: string;
}

export const LatestSelection = ({
  selectorConfig,
  productKey,
  productRoute,
  contentfulId,
}: Props) => {
  const location = useLocation();
  const [login] = useSystemPages('login');
  const isLoggedIn = useSelector(authSelectors.isLoggedIn);
  const intl = useIntl();
  const { url } = useLatestSelection(productKey);
  const [isLoadingProductRoute, setIsLoadingProductRoute] = useState(true);
  const [validatedProductRoute, setValidatedProductRoute] = useState<ProductRouteItem>();
  const customerGroupId = useReduxSelector<RootState, string | undefined>(
    // @ts-expect-error - The customer slice needs to be converted to typescript
    ({ customer }) => customer?.customer?.customerGroup?.id,
  );
  const dispatchLoginDataLayerEvent = () => {
    dispatchDataLayerEntry(
      'product_detail_page',
      'login_button_clicked',
      { type: 'login_button_clicked' },
    );
  };

  const validateProductRoutes = async (updatedProductRoute: IProductRoute) => {
    const [validProductRoute] = await validateRoutes([updatedProductRoute], contentfulId, customerGroupId);

    setValidatedProductRoute(validProductRoute);
    setIsLoadingProductRoute(false);
  };

  useEffect(() => {
    if (url && !validatedProductRoute) {
      const updatedProductRoute = klona(productRoute);
      updatedProductRoute.productCode.internal.content = url.search;
      updatedProductRoute.label.displayName = intl.formatMessage({ id: 'product.configurator.recent.order', defaultMessage: 'Recent Order' });
      updatedProductRoute.label.type = 'recently-ordered';
      updatedProductRoute.routeIdentifier = `${productKey} / recently-ordered / auto-generated`;
      updatedProductRoute.isAutoGenerated = true;

      validateProductRoutes(updatedProductRoute);
    }
  }, [url]);

  if (!isLoggedIn) {
    return (
      <SegmentVariants variantFilters={variantFilters}>
        <Heading as="h4" className="pd-mt-5">
          <FormattedMessage id="product.configurator.recent.selection" defaultMessage="Your most recent selection" />
        </Heading>

        <FormattedMessage
          id="product.configurator.recent.login.incentive"
          defaultMessage="<a>Login</a> to quickly select your most recent selection of this product"
          values={{
            a: (chunks: any) => (
              <Link to={login.path} onClick={dispatchLoginDataLayerEvent} className="pd-underline" state={{ redirectUrl: location.pathname }}>
                {chunks}
              </Link>
            ),
          }}
        />
      </SegmentVariants>
    );
  }

  if (url && validatedProductRoute) {
    return (
      <SegmentVariants variantFilters={variantFilters}>
        <Heading as="h4" className="pd-mt-5">
          <FormattedMessage id="product.configurator.recent.selection" defaultMessage="Your most recent selection" />
        </Heading>
        {isLoadingProductRoute ? (
          <div className="pd-my-1 pd-flex pd-justify-center">
            <Spinner
              size="sm"
              variant="dim"
              screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
            />
          </div>
        ) : (
          <ProductRoute
            key={productKey}
            productRoute={validatedProductRoute.productRoute}
            selector={selectorConfig}
            price={validatedProductRoute.price}
            originalPrice={validatedProductRoute.originalPrice}
          />
        )}
      </SegmentVariants>
    );
  }

  return null;
};
